import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  LayoutDashboard,
  MessagesSquare,
  Plug,
  ServerCog,
  Sparkles,
  Table2,
} from 'lucide-react';
import { SectionHeading } from '../../components/SectionHeading';
import { useSpotlight } from '../../hooks';

type Size = 'feature' | 'wide' | 'unit';

interface Service {
  icon: typeof Bot;
  tone: string;
  size: Size;
  title: string;
  body: string;
  meta?: string;
  featured?: boolean;
  /** Concrete examples — fills the larger tiles with real detail. */
  examples?: string[];
}

const SERVICES: Service[] = [
  {
    icon: Bot,
    tone: 'g-blue',
    size: 'feature',
    featured: true,
    title: 'Agentic AI workflows',
    body: 'Multi-step agents that triage, decide, and act — across your email, CRM, billing, and tools. Like an ops manager that never sleeps. They work your queue overnight and hand you only the things that genuinely need a person.',
    examples: [
      'triage inbound email',
      'update the CRM',
      'chase overdue invoices',
      'draft the quote',
      'tag and route contacts',
      'escalate to a human',
    ],
    meta: 'Powers Pulse, our WhatsApp agent',
  },
  {
    icon: Sparkles,
    tone: 'g-violet',
    size: 'wide',
    title: 'Custom SaaS & web apps',
    body: "Real product-grade software — designed, built, and shipped. Whether it's an internal platform or a customer-facing app.",
    examples: ['ERP', 'client portals', 'audit tooling', 'asset tracking'],
    meta: 'Lumen ERP · 48by40 · SOC 2 controls testing',
  },
  {
    icon: Plug,
    tone: 'g-pink',
    size: 'unit',
    title: 'AI integrations',
    body: 'Wire intelligence into the systems you already run — HubSpot, QuickBooks, Slack, Notion, Stripe, anything with an API.',
  },
  {
    icon: LayoutDashboard,
    tone: 'g-sun',
    size: 'unit',
    title: 'Internal tools & dashboards',
    body: 'Admin panels, ops boards, approval flows, live KPIs. Everything your team currently runs in their head or on Friday calls.',
  },
  {
    icon: MessagesSquare,
    tone: 'g-blue',
    size: 'wide',
    title: 'AI chatbots & assistants',
    body: 'Internal copilots and customer-facing assistants, trained on your docs, processes, and brand — answering from your material rather than guessing.',
    examples: ['support assistant', 'internal copilot', 'onboarding guide', 'policy lookup'],
    meta: 'RAG over your PDFs and data',
  },
  {
    icon: Table2,
    tone: 'g-violet',
    size: 'unit',
    title: 'Spreadsheet → portal',
    body: 'Trapped in a tangle of XLSX? We turn the worst of them into a real, branded portal in days.',
  },
  {
    icon: ServerCog,
    tone: 'g-pink',
    size: 'unit',
    title: 'Managed AI operations',
    body: "We don't just ship and leave. Monitoring, retraining, and new features as your business grows.",
    meta: 'AWS · DigitalOcean · Railway · Hetzner · PythonAnywhere',
  },
];

export function Services(): React.JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  useSpotlight(gridRef);

  return (
    <section id="services" className="section-surface">
      <div className="container">
        <SectionHeading
          anim="rise"
          title={
            <>
              Seven things, done <span className="si accent-text">properly</span>.
            </>
          }
          lede="Every engagement is one of these — or a combination. Scoped tightly, shipped to production, supported after launch."
        />

        <div className="bento" ref={gridRef}>
          {SERVICES.map((s, i) => (
            <Link
              key={s.title}
              to="/#contact"
              className={`bento-card ${s.size} reveal`}
              data-spotlight
              data-delay={(i % 3) + 1}
              aria-label={`Start a project about ${s.title.toLowerCase()}`}
            >
              <div className="bento-top">
                <span className="bento-num">{String(i + 1).padStart(2, '0')}</span>
                {s.featured && <span className="bento-badge">Most popular</span>}
                <ArrowRight className="bento-arrow" size={18} aria-hidden="true" />
              </div>

              <div className={`bento-icon ${s.tone}`} aria-hidden="true">
                <s.icon size={s.size === 'feature' ? 26 : 22} strokeWidth={1.8} />
              </div>

              <h3 className="h3">{s.title}</h3>
              <p>{s.body}</p>
              {s.examples && (
                <ul className="bento-examples">
                  {s.examples.map((ex) => (
                    <li key={ex}>{ex}</li>
                  ))}
                </ul>
              )}
              {s.meta && <span className="bento-meta">{s.meta}</span>}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
